/**
 * Primitive inventory — normative source 07-11-design-system.md
 * §Primitive Inventory and §Primitive State Matrix.
 *
 * Twenty-eight primitives. Every one carries the state matrix below; `true`
 * means the primitive must render the state, `false` means the state does not
 * apply and the primitive must not invent one.
 */

export const PRIMITIVE_IDS = [
  'PRM-01',
  'PRM-02',
  'PRM-03',
  'PRM-04',
  'PRM-05',
  'PRM-06',
  'PRM-07',
  'PRM-08',
  'PRM-09',
  'PRM-10',
  'PRM-11',
  'PRM-12',
  'PRM-13',
  'PRM-14',
  'PRM-15',
  'PRM-16',
  'PRM-17',
  'PRM-18',
  'PRM-19',
  'PRM-20',
  'PRM-21',
  'PRM-22',
  'PRM-23',
  'PRM-24',
  'PRM-25',
  'PRM-26',
  'PRM-27',
  'PRM-28',
] as const;

export type PrimitiveId = (typeof PRIMITIVE_IDS)[number];

export const PRIMITIVE_COMPONENTS = {
  Button: 'PRM-01',
  Link: 'PRM-02',
  Input: 'PRM-03',
  Textarea: 'PRM-04',
  NumericInput: 'PRM-05',
  Select: 'PRM-06',
  Combobox: 'PRM-07',
  Checkbox: 'PRM-08',
  Radio: 'PRM-09',
  Switch: 'PRM-10',
  FormField: 'PRM-11',
  Fieldset: 'PRM-12',
  Badge: 'PRM-13',
  Tag: 'PRM-14',
  Avatar: 'PRM-15',
  Icon: 'PRM-16',
  Spinner: 'PRM-17',
  Skeleton: 'PRM-18',
  Progress: 'PRM-19',
  Tooltip: 'PRM-20',
  Card: 'PRM-21',
  ListRow: 'PRM-22',
  DataTable: 'PRM-23',
  Tabs: 'PRM-24',
  Dialog: 'PRM-25',
  Drawer: 'PRM-26',
  Toast: 'PRM-27',
  Banner: 'PRM-28',
} as const satisfies Record<string, PrimitiveId>;

export type PrimitiveComponentName = keyof typeof PRIMITIVE_COMPONENTS;

export const PRIMITIVE_NAMES: Record<PrimitiveId, string> = {
  'PRM-01': 'Button',
  'PRM-02': 'Link',
  'PRM-03': 'Text input',
  'PRM-04': 'Textarea',
  'PRM-05': 'Numeric input',
  'PRM-06': 'Select',
  'PRM-07': 'Combobox',
  'PRM-08': 'Checkbox',
  'PRM-09': 'Radio group',
  'PRM-10': 'Toggle',
  'PRM-11': 'Field',
  'PRM-12': 'Fieldset',
  'PRM-13': 'Badge',
  'PRM-14': 'Tag',
  'PRM-15': 'Avatar',
  'PRM-16': 'Icon',
  'PRM-17': 'Spinner',
  'PRM-18': 'Skeleton',
  'PRM-19': 'Progress',
  'PRM-20': 'Tooltip',
  'PRM-21': 'Card',
  'PRM-22': 'List row',
  'PRM-23': 'Table',
  'PRM-24': 'Tabs',
  'PRM-25': 'Dialog',
  'PRM-26': 'Drawer',
  'PRM-27': 'Toast',
  'PRM-28': 'Banner',
};

export const PRIMITIVE_STATES = [
  'default',
  'hover',
  'focus',
  'active',
  'disabled',
  'loading',
  'error',
  'empty',
] as const;

export type PrimitiveState = (typeof PRIMITIVE_STATES)[number];

/** Themes a primitive may render in. Absence is a declaration, not an omission. */
export const PRIMITIVE_THEMES: Record<PrimitiveId, readonly ('standard' | 'floor' | 'dense')[]> = {
  'PRM-01': ['standard', 'floor', 'dense'],
  'PRM-02': ['standard', 'floor', 'dense'],
  'PRM-03': ['standard', 'floor', 'dense'],
  'PRM-04': ['standard', 'dense'],
  'PRM-05': ['standard', 'floor', 'dense'],
  'PRM-06': ['standard', 'floor', 'dense'],
  'PRM-07': ['standard', 'dense'],
  'PRM-08': ['standard', 'floor', 'dense'],
  'PRM-09': ['standard', 'floor', 'dense'],
  'PRM-10': ['standard', 'dense'],
  'PRM-11': ['standard', 'floor', 'dense'],
  'PRM-12': ['standard', 'floor', 'dense'],
  'PRM-13': ['standard', 'floor', 'dense'],
  'PRM-14': ['standard', 'dense'],
  'PRM-15': ['standard', 'dense'],
  'PRM-16': ['standard', 'floor', 'dense'],
  'PRM-17': ['standard', 'floor', 'dense'],
  'PRM-18': ['standard', 'dense'],
  'PRM-19': ['standard', 'floor', 'dense'],
  'PRM-20': ['standard', 'dense'],
  'PRM-21': ['standard', 'floor', 'dense'],
  'PRM-22': ['standard', 'floor', 'dense'],
  'PRM-23': ['standard', 'dense'],
  'PRM-24': ['standard', 'dense'],
  'PRM-25': ['standard', 'floor', 'dense'],
  'PRM-26': ['standard', 'dense'],
  'PRM-27': ['standard', 'dense'],
  'PRM-28': ['standard', 'floor', 'dense'],
};

type StateRow = Record<PrimitiveState, boolean>;

const row = (bits: string): StateRow =>
  Object.fromEntries(PRIMITIVE_STATES.map((s, i) => [s, bits[i] === '1'])) as StateRow;

/** Order of bits: default, hover, focus, active, disabled, loading, error, empty. */
export const PRIMITIVE_STATE_MATRIX: Record<PrimitiveId, StateRow> = {
  'PRM-01': row('11111100'),
  'PRM-02': row('11111000'),
  'PRM-03': row('11101110'),
  'PRM-04': row('11101110'),
  'PRM-05': row('11101110'),
  'PRM-06': row('11111111'),
  'PRM-07': row('11111111'),
  'PRM-08': row('11111010'),
  'PRM-09': row('11111010'),
  'PRM-10': row('11111110'),
  'PRM-11': row('10101110'),
  'PRM-12': row('10001010'),
  'PRM-13': row('10000000'),
  'PRM-14': row('11111000'),
  'PRM-15': row('10000111'),
  'PRM-16': row('10001000'),
  'PRM-17': row('10000100'),
  'PRM-18': row('00000100'),
  'PRM-19': row('10000110'),
  'PRM-20': row('11100000'),
  'PRM-21': row('11111111'),
  'PRM-22': row('11111110'),
  'PRM-23': row('11111111'),
  'PRM-24': row('11111111'),
  'PRM-25': row('10100110'),
  'PRM-26': row('10100111'),
  'PRM-27': row('11100000'),
  'PRM-28': row('10100000'),
};

export function requiredStates(id: PrimitiveId): PrimitiveState[] {
  return PRIMITIVE_STATES.filter((s) => PRIMITIVE_STATE_MATRIX[id][s]);
}
